import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Scale, RefreshCw, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, Search, FileSpreadsheet, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";
import { TBGapDialog } from "@/components/TBGapDialog";
import { TieAccountsDialog } from "@/components/TieAccountsDialog";
import { useAuth } from "@/context/AuthContext";

const Badge = ({ ok, explained, testId, onClick }) => {
  const good = ok || explained;
  return (
    <span data-testid={testId} onClick={onClick} role={onClick ? "button" : undefined}
      title={explained && !ok ? "Difference fully explained by timing items (cleared since / posted after the As-on date)" : !ok && onClick ? "Show the documents behind this difference" : undefined}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${good ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"} ${!ok && onClick ? "cursor-pointer hover:bg-amber-100 underline-offset-2 hover:underline" : ""}`}>
      {good ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{ok ? "Matched" : explained ? "Explained" : "Difference"}
    </span>
  );
};
const explainedOf = (r) => !r.matched && r.unexplained !== undefined && Math.abs(r.unexplained) <= 1;
const timingOf = (r) => (r.unexplained === undefined ? 0 : Math.round((r.tb_balance - r.ageing_total - r.unexplained) * 100) / 100);

const Amt = ({ v, tone = "" }) => <td className={`py-1.5 pr-4 text-right tabular-nums ${tone}`}>{v === 0 ? "—" : inr(v)}</td>;

export const TBReconciliation = ({ side = "ap", asOn, basis, onSelectGroup, testPrefix, version = 0, onData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(true);
  const [showNotes, setShowNotes] = useState(false);
  const [gapGroup, setGapGroup] = useState(null);
  const [dl, setDl] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const { user } = useAuth();
  const canTie = user?.role === "admin" || !!user?.can_tie;

  const downloadBridge = async () => {
    setDl(true);
    try {
      const params = { basis, ...(asOn && asOn !== "today" ? { as_on: asOn } : {}) };
      const r = await axios.get(`${API}/reports/${side}-ageing/tb-reconciliation/bridge`, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${side}-ageing-tb-bridge-${data?.as_on || "today"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not build the bridge statement");
    } finally { setDl(false); }
  };

  const load = async (refresh = false) => {
    setLoading(true); setError("");
    try {
      const params = { basis, ...(asOn && asOn !== "today" ? { as_on: asOn } : {}), ...(refresh ? { refresh: true } : {}) };
      const r = await axios.get(`${API}/reports/${side}-ageing/tb-reconciliation`, { params });
      setData(r.data);
      onData?.(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load the Trial Balance reconciliation");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(version > 0); }, [asOn, basis, side, version]);
  useEffect(() => {
    if (!data?.backfill?.running) return undefined;
    const id = setTimeout(() => load(false), 6000);
    return () => clearTimeout(id);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data]);

  const t = data?.totals;
  const who = side === "ap" ? "supplier" : "customer";
  return (
    <Card className="border-slate-200 shadow-sm fade-in-up" data-testid={`${testPrefix}-tb-recon`}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Scale className="h-4 w-4" /></div>
          <div>
            <CardTitle className="text-base font-semibold text-slate-800">Trial Balance reconciliation</CardTitle>
            <p className="text-xs text-slate-500">Ageing total per {who} reconciliation G/L vs SAP Trial Balance closing balance{data?.as_on ? ` as on ${data.as_on}` : ""}{data?.tb_fetched_at ? ` · TB read ${new Date(data.tb_fetched_at).toLocaleTimeString()}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {t && <Badge ok={t.matched} explained={!t.matched && t.ties} testId={`${testPrefix}-tb-overall-badge`} />}
          {canTie && (
            <button type="button" onClick={() => setPickOpen(true)} disabled={!data?.available} data-testid={`${testPrefix}-tb-choose-accounts`} title="Tick which G/L accounts count toward the tie (for everyone)"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <ListChecks className="h-3.5 w-3.5" />Tie accounts{data?.scope ? ` (${data.scope.length})` : ""}
            </button>
          )}
          <button type="button" onClick={downloadBridge} disabled={dl || !data?.available} data-testid={`${testPrefix}-tb-bridge-download`} title="TB → adjustments → Ageing by account, with the documents behind each line"
            className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
            {dl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}Bridge (Excel)
          </button>
          <button type="button" onClick={() => load(true)} disabled={loading} data-testid={`${testPrefix}-tb-refresh`} title="Re-read the Trial Balance from SAP"
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Refresh TB
          </button>
          <button type="button" onClick={() => setOpen(!open)} data-testid={`${testPrefix}-tb-toggle`} className="text-slate-500 hover:text-slate-800">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          {error && <p className="text-sm text-red-600" data-testid={`${testPrefix}-tb-error`}>{error}</p>}
          {!error && data && !data.available && <p className="text-sm text-slate-500" data-testid={`${testPrefix}-tb-unavailable`}>{data.reason}</p>}
          {!error && data?.available && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid={`${testPrefix}-tb-table`}>
                  <thead>
                    <tr className="text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                      <th className="text-left py-2 pr-4">TB group (Recon G/L)</th>
                      <th className="text-right py-2 pr-4">Docs</th>
                      <th className="text-right py-2 pr-4">Ageing total</th>
                      <th className="text-right py-2 pr-4">SAP open items</th>
                      <th className="text-right py-2 pr-4">TB balance</th>
                      <th className="text-right py-2 pr-4">Difference</th>
                      <th className="text-right py-2 pr-4" title="Bills open on the As-on date and cleared since, posted / dated after it, outside the ageing scope">Timing items</th>
                      <th className="text-left py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.gl_account || "pending"} data-testid={`${testPrefix}-tb-row-${r.gl_account || "pending"}`}
                        onClick={() => r.gl_account && onSelectGroup?.(r)} title={r.gl_account ? "Open this group in the drill-down" : "Re-sync SAP to stamp the reconciliation G/L on these documents"}
                        className={`border-b border-slate-100 ${r.gl_account ? "cursor-pointer hover:bg-slate-50" : "bg-amber-50/40"} ${r.in_scope === false ? "opacity-50" : ""}`}>
                        <td className="py-1.5 pr-4 font-semibold text-slate-800">{r.name || `G/L ${r.gl_account}`}{r.gl_account && <span className="ml-1 font-mono font-normal text-slate-400">{r.gl_account}</span>}{!r.in_tb && r.gl_account && <span className="ml-1 text-[10px] text-slate-400">(not in TB)</span>}{r.in_scope === false && <span className="ml-1 text-[10px] text-slate-500" data-testid={`${testPrefix}-tb-excluded-${r.gl_account}`}>(not in tie)</span>}</td>
                        <td className="py-1.5 pr-4 text-right text-slate-600">{r.ageing_count}</td>
                        <Amt v={r.ageing_total} tone="font-semibold text-slate-800" />
                        <Amt v={r.sap_open} tone="text-slate-600" />
                        <Amt v={r.tb_balance} tone="text-slate-800" />
                        {!r.matched && r.gl_account ? (
                          <td className="py-1.5 pr-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => setGapGroup(r)} title="List the documents behind this difference" data-testid={`${testPrefix}-tb-diff-${r.gl_account}`}
                              className="inline-flex items-center gap-1 tabular-nums text-amber-700 font-semibold hover:underline">{inr(r.diff)}<Search className="h-3 w-3" /></button>
                          </td>
                        ) : <Amt v={r.diff} tone={Math.abs(r.diff) <= 1 ? "text-emerald-600" : "text-amber-700 font-semibold"} />}
                        <Amt v={timingOf(r)} tone="text-slate-600" />
                        <td className="py-1.5" onClick={(e) => e.stopPropagation()}>
                          <Badge ok={r.matched} explained={explainedOf(r)} testId={`${testPrefix}-tb-badge-${r.gl_account || "pending"}`} onClick={!r.matched && r.gl_account ? () => setGapGroup(r) : undefined} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 font-bold text-slate-900" data-testid={`${testPrefix}-tb-total-row`}>
                      <td className="py-2 pr-4">Total{t.excluded?.accounts ? " (accounts in the tie)" : ""} · {t.matched_groups}/{t.groups} groups matched</td>
                      <td className="py-2 pr-4 text-right">{t.ageing_count}</td>
                      <Amt v={t.ageing_total} /><Amt v={t.sap_open} /><Amt v={t.tb_balance} />
                      <Amt v={t.diff} tone={t.matched || t.ties ? "text-emerald-700" : "text-amber-700"} />
                      <Amt v={timingOf(t)} />
                      <td className="py-2"><Badge ok={t.matched} explained={!t.matched && t.ties} /></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {data.backfill?.running && <p className="mt-2 text-[11px] text-sky-700" data-testid={`${testPrefix}-tb-backfill`}>Stamping the reconciliation G/L on earlier documents from SAP — this panel refreshes automatically.</p>}
              {data.pending?.count > 0 && !data.backfill?.running && <p className="mt-2 text-[11px] text-amber-700" data-testid={`${testPrefix}-tb-pending`}>{data.pending.count} document(s) worth {inr(data.pending.total)} have no reconciliation G/L yet — run an SAP sync to stamp them.</p>}
              <button type="button" onClick={() => setShowNotes(!showNotes)} className="mt-2 text-[11px] text-slate-500 underline-offset-2 hover:underline" data-testid={`${testPrefix}-tb-notes-toggle`}>{showNotes ? "Hide" : "How to read this"}</button>
              {showNotes && <ul className="mt-1 list-disc pl-5 text-[11px] text-slate-500 space-y-0.5" data-testid={`${testPrefix}-tb-notes`}>{data.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
            </>
          )}
        </CardContent>
      )}
      <TBGapDialog side={side} group={gapGroup} asOn={asOn} onClose={() => setGapGroup(null)} testPrefix={testPrefix} />
      <TieAccountsDialog side={side} data={data} open={pickOpen} onClose={() => setPickOpen(false)} onSaved={() => load(false)} testPrefix={testPrefix} />
    </Card>
  );
};
